import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CountryInfo, GlobalCultureState } from '../interfaces/product';
import { StorageService } from './storage.service';
import { CurrencyService } from './currency.service';

/// Locale/me's answer for the visitor, asked by the server render and carried in the page (StoreService).
const VISITOR_LOCALE = makeStateKey<CountryInfo>('visitor-locale');

@Injectable({
    providedIn: 'root'
})
export class StoreService 
{
    // The shop's own contact details are the Company section's (CompanyService); the theme's
    // placeholder address, phone numbers and hours that sat here were never true.

    /// The currency this browser was last told to show prices in (US dollars too), as a cookie the server render reads
    /// (the owner, 2026-09-30: prices drew in US dollars and changed to the visitor's currency on every load, and a
    /// product page's price and delivery changed again when the visitor's own arrived). With it, the render asks
    /// Locale/me for the visitor too and draws the page for them - their currency, and on a product page their own
    /// price, stock and delivery (ProductComponent) - and carries what it asked in the page, so the browser's first draw
    /// is the same. On PrimeHat the render asks for every page, cookie or not (askForTheVisitorOnTheServer): everybody
    /// gets the same answer, Bangladesh in taka, so a first visit and a crawler see taka too.
    static readonly shownCurrencyCookie = 'izz_cur';

    /// On the server: this page is drawn for the visitor who asked for it, so what the page asks about them may be
    /// carried to the browser (TransferState). On PrimeHat every page: the answer is Bangladesh's for everybody.
    drawnForVisitor = false;

    private defaultCountry:CountryInfo = this.firstCountry();
    public countryInfo:CountryInfo; 
    private baseUrl = environment.apiUrl;
    private onCountryInfoChanged$: BehaviorSubject<CountryInfo> = new BehaviorSubject(this.defaultCountry);  

    readonly onCountry:Observable<CountryInfo> = this.onCountryInfoChanged$.asObservable();

    /// Bump this whenever the cached cultureState shape changes, or to force every
    /// existing browser to refetch reference data it has stored in localStorage.
    /// 3: the country list is Bangladesh only (2026-10-15).
    private static readonly CULTURE_STATE_VERSION = 3;

    onCultureDataChange$: BehaviorSubject<any>;
    cultureState: GlobalCultureState = 
	{
		typeInfos: {
			"Account.Type":<any[]> Array(), 
			"System.Country":<any[]> Array(),
			"System.Language":<any[]> Array(),
			"System.WorldRegion":<any[]> Array(), 
			"System.Currency": <any[]>Array()
		},
		culture:{ },
		currentCulture:'en',
		supporterdCultureCodes:<any[]> Array(), 
	} 
    
    constructor(private http:HttpClient, private storage:StorageService, private state:TransferState,
                @Inject(PLATFORM_ID) private platformId:any, @Optional() @Inject(REQUEST) private request:any)
    { 
        this.onCultureDataChange$ = new BehaviorSubject(null);
        this.init(); 
        this.askForTheVisitorOnTheServer();
    }

    /// What prices go by until Locale/me answers: in the browser, the answer the server render drew the page with, when
    /// it carried one - so the first draw here matches the page already on screen - else the one kept from an earlier
    /// visit (getCountryInfoFromStorage).
    private firstCountry(): CountryInfo
    {
        if (isPlatformBrowser(this.platformId))
        {
            const carried = this.state.get<CountryInfo>(VISITOR_LOCALE, null);
            if (carried && carried.CurrencyMultiplier > 0)
            {
                // Stamped by this device's clock, as every answer is (getCountryInfo).
                return { ...carried, RateSeenAtMs: Date.now() };
            }
        }
        return this.getCountryInfoFromStorage();
    }

    /// On the server, for every page: Locale/me - Bangladesh in taka for everybody on PrimeHat (SiteCountry), a first
    /// visit and a crawler included - and the page is drawn for that answer, which it carries. The render waits for the
    /// call; if it fails, the page is drawn in US dollars.
    private askForTheVisitorOnTheServer(): void
    {
        if (!isPlatformServer(this.platformId) || !this.request || !this.request.headers) { return; }

        this.drawnForVisitor = true;
        this.http.get<CountryInfo>(this.baseUrl + '/api/Locale/me').subscribe(res =>
        {
            if (res && res.CurrencyMultiplier > 0 && res.CurrencyCode)
            {
                this.state.set(VISITOR_LOCALE, res);
                this.onCountryInfoChanged$.next({ ...res, RateSeenAtMs: Date.now() });
            }
        }, () => { /* drawn in US dollars */ });
    }

    /// One cookie's value from a Cookie header, or ''.
    static cookieIn(header: string, name: string): string
    {
        const m = new RegExp('(?:^|;\\s*)' + name + '=([^;]*)').exec(header || '');
        try { return m ? decodeURIComponent(m[1]).trim() : ''; }
        catch { return ''; }
    }

    /// Keeps the currency Locale/me just told this browser to show - US dollars included - for the server render to draw
    /// the next page for this visitor; an answer that cannot be used takes the cookie away.
    private rememberShownCurrency(info: CountryInfo): void
    {
        if (!isPlatformBrowser(this.platformId)) { return; }
        try
        {
            const code = info && info.CurrencyMultiplier > 0 && /^[A-Z]{3}$/.test(info.CurrencyCode || '') ? info.CurrencyCode : '';
            document.cookie = StoreService.shownCurrencyCookie + '=' + code + '; path=/; SameSite=Lax; max-age=' + (code ? 31536000 : 0);
        }
        catch { /* a browser that refuses cookies is drawn the default figures first, as before */ }
    }

    init()
    {
        var oldState = this.storage.getItem("u_cultureState");

        try
        {
            // Only trust the cached copy if it actually carries the reference data.
            //
            // This used to accept ANY cached state and never call the API again, so a
            // browser that once stored an empty list - because the server had no country
            // rows yet - kept an empty country dropdown forever, no matter what the API
            // returned afterwards. localStorage has no expiry, so it never self-corrected.
            // The same would happen to real users whenever countries or languages change.
            //
            // Bumping CULTURE_STATE_VERSION forces every existing browser to refetch.
            if(oldState && oldState !== null && this.isUsableCultureState(JSON.parse(oldState)))
            {
                this.cultureState = JSON.parse(oldState);
                this.onCultureDataChange$.next(this.cultureState);
            }
            else
            {
                this.getAndInitTypeInfo("en").subscribe(
                {
                    complete:()=>
                    {
            
                    }
                });
            }
        }
        catch
        {
            this.getAndInitTypeInfo("en").subscribe(
            {
                complete:()=>
                {
        
                }
            });
        } 
    }

    /// A cached cultureState is only usable if it was written by the current version
    /// AND actually contains countries. An empty country list means it was cached
    /// before the server had the data, and reusing it would keep the dropdown empty
    /// permanently.
    private isUsableCultureState(state:any): boolean
    {
        try
        {
            if(!state || state.__v !== StoreService.CULTURE_STATE_VERSION) { return false; }
            const countries = state.typeInfos && state.typeInfos["System.Country"];
            return Array.isArray(countries) && countries.length > 0;
        }
        catch
        {
            return false;
        }
    }

    getAndInitTypeInfo(culture:string): Observable<any>
	{
		return this.http.get<any>(this.baseUrl +`/api/Culture/GetTypeInfos/${culture}`, {}).pipe(map((res:any) => 
        { 
            console.log(res);
			 if(res && res.Counts > 0)
			 { 
                let nxtTypeInfos = Object.assign({}, this.cultureState.typeInfos);
                for (let key in nxtTypeInfos) 
                { 
                    nxtTypeInfos[key] = res.Result.filter(f=>f.Type == key);
                    nxtTypeInfos[key] = ((key==='System.Country')|| (key==='System.Language') || (key==='System.WorldRegion') || (key==='System.Currency')?
                    nxtTypeInfos[key].sort((a, b) => (a.Name > b.Name) ? 1 : -1) :nxtTypeInfos[key].sort((a, b) => (a.Order > b.Order) ? 1 : -1))
                }
                this.cultureState.typeInfos = nxtTypeInfos;
                this.cultureState.currentCulture = culture;
                // Stamp the version so isUsableCultureState can reject copies written by
                // an older build when the cached shape changes.
                (this.cultureState as any).__v = StoreService.CULTURE_STATE_VERSION;
                this.storage.setItem("u_cultureState", JSON.stringify(this.cultureState));

                console.log(this.cultureState); 
                this.getAndInitSupportedCulture("en").subscribe(
                {
                    complete:()=>
                    {
                        
                    }
                });
			 }
        }));
	}

    // The labels are the culture system's (shared/culture/CultureService), loaded from a published file; the old
    // Culture/GetCultureData answer went into cultureState.culture, which nothing read.

    getAndInitSupportedCulture(culture:string): Observable<any> 
	{
		return this.http.get<any>(this.baseUrl +`/api/Culture/SuportedCultureCodes/${culture}`, {}).pipe(map((res:any) => 
        {  
            if(res)
            {  
                this.cultureState.supporterdCultureCodes = res;
                this.onCultureDataChange$.next(this.cultureState);
                this.storage.setItem("u_cultureState", JSON.stringify(this.cultureState));
                console.log(this.cultureState);
            }
        }));
	} 

    setCountryInfoToStorage(country: CountryInfo): void 
    {
        try{
            const countryJSON = JSON.stringify(country);
            this.storage.setItem('defaultCn', countryJSON);
        }
        catch{}
    }

    /// A rate older than this is not shown - the server's rule (ExchangeRates.MaxAge) - so a country answer kept from an
    /// earlier visit shows US dollars until a fresh one arrives.
    static readonly RateMaxAgeMs = 60 * 60 * 1000;

    /// The rate's age is what the server said it was plus the time since, measured on this device's clock alone - never
    /// the server's RateAtUtc against this clock, which a device set to the wrong time (or zone) would read as hours
    /// off, showing it dollars for good or an old rate. A clock turned back past a minute counts as too old.
    static rateIsFresh(info: CountryInfo): boolean {
        if (!info || !info.CurrencyCode || info.CurrencyCode === (info.SiteCurrencyCode || CurrencyService.site.code)) { return true; }
        if (typeof info.RateAgeSeconds !== 'number' || typeof info.RateSeenAtMs !== 'number') { return false; }
        const since = Date.now() - info.RateSeenAtMs;
        return since > -60 * 1000 && info.RateAgeSeconds * 1000 + Math.max(0, since) <= StoreService.RateMaxAgeMs;
    }

    /// The same country in the site's own currency, as prices are stored - for an answer whose rate is too old to show.
    static inSiteCurrency(info: CountryInfo): CountryInfo {
        const site = CurrencyService.site;
        return { ...info, CurrencyCode: info?.SiteCurrencyCode || site.code, CurrencySymbol: info?.SiteCurrencySymbol || site.symbol,
            CurrencyMultiplier: 1, CurrencyDecimals: info?.SiteCurrencyDecimals ?? site.decimals,
            RateAtUtc: null, RateAgeSeconds: null, RateSeenAtMs: null };
    }

    getCountryInfoFromStorage(): CountryInfo | null 
    { 
        try{
            const countryJSON = this.storage.getItem('defaultCn');
            if (countryJSON) {
                const kept = JSON.parse(countryJSON) as CountryInfo;
                return StoreService.rateIsFresh(kept) ? kept : StoreService.inSiteCurrency(kept);
            }
        }
        catch{}
        // Nothing kept, and Locale/me has not answered yet. Prices go by the stored amounts meanwhile, in whatever is
        // known of the site's currency, and the country is unknown, marked Pending: the flag's place stays empty until
        // the answer names the site's country.
        var def:CountryInfo ={
            Name:"",
            CountryCode:"",
            CurrencyCode:CurrencyService.site.code,
            CurrencySymbol:CurrencyService.site.symbol,
            CurrencyMultiplier:1,
            Pending:true
        }
        return def;
    }

    /// An answer that cannot be used (Locale/me never gives one) counts as none: the guess is shown as the country.
    setCountryInfo(info:CountryInfo)
    {
        if(info && info.CurrencyMultiplier > 0)
        {
            this.setCountryInfoToStorage(info);
            this.onCountryInfoChanged$.next(info);
        }
        else
        {
            this.fallBackToDefaultCountry();
        }
    }

    /// Locale/me could not be asked: the guess becomes this page's country and is shown - the United States, as the
    /// server says when it cannot tell - rather than an empty flag for good. Not kept, so the next page asks again. A
    /// country already known, from this visit or an earlier one, stays.
    fallBackToDefaultCountry(): void
    {
        const current = this.onCountryInfoChanged$.value;
        if (current && !current.Pending) { return; }
        this.onCountryInfoChanged$.next({ ...(current || this.getCountryInfoFromStorage()), Pending: false });
    }

    /// The answer stamped with when it arrived, by this device's clock - what rateIsFresh counts the rate's age from.
    getCountryInfo(): Observable<any>
	{
		return this.http.get<any>(this.baseUrl + `/api/Locale/me`, {}).pipe(
            map(res =>
            {
                this.rememberShownCurrency(res);
                return res ? { ...res, RateSeenAtMs: Date.now() } : res;
            }));
	}

    getMe2(p:string): Observable<any> 
	{
		return this.http.get<any>(this.baseUrl + `/api/Locale/me2?p=${p}`, {});
	}
}
